import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  connectWhatsApp,
  connectWhatsAppOAuth,
  getWhatsAppConnection,
  verifyWhatsAppConnection,
  disconnectWhatsApp
} from '../../../actions/template'
import TemplatesTable from '../../tables/TemplatesTable/TemplatesTable'
import { ReactComponent as WhatsappIcon } from '../../../assets/whatsapp.svg'
import { ReactComponent as FacebookIcon } from '../../../assets/facebook.svg'
import { ReactComponent as PhoneIcon } from '../../../assets/phone.svg'
import { ReactComponent as InfoIcon } from '../../../assets/info.svg'
import './TemplatesTab.css'

// ─── Constants ────────────────────────────────────────────────────────────────

const META_APP_ID = process.env.REACT_APP_META_APP_ID || '1097147992427164'
const META_CHANNEL_URL = `${window.location.origin}/channel.html`

const EMPTY_WA = {
  wabaId: '',
  phoneNumberId: '',
  displayPhoneNumber: '',
  accessToken: '',
  templatesNamespace: '',
  businessId: ''
}

const FIELD_HINTS = {
  wabaId: 'Encuéntralo en Meta Business Suite → Cuentas → WhatsApp Business.',
  phoneNumberId: 'ID numérico del número en Meta, distinto al número de teléfono.',
  displayPhoneNumber: 'Número en formato internacional E.164. Ej: +573001234567',
  accessToken: 'Usa un token de System User (sin expiración). No uses tokens de usuario personal.',
  templatesNamespace: 'Namespace del WABA. Se obtiene automáticamente si lo dejas vacío.',
  businessId: 'ID del Business Manager en Meta. Opcional.'
}

const META_ERROR_MESSAGES = {
  190: 'El token de acceso venció o fue revocado. Reconecta tu cuenta desde Meta.',
  200: 'El token no tiene el permiso whatsapp_business_management. Verifica los permisos del System User.',
  100: 'Parámetro inválido. Verifica que el WABA ID y Phone Number ID sean correctos.',
  803: 'El objeto no existe en Meta. Confirma que el WABA ID corresponda a tu cuenta.',
  10: 'Permiso denegado. El System User debe tener rol de Admin en el WABA.'
}

const WIZARD_STEPS = [
  { label: 'Requisitos' },
  { label: 'Número' },
  { label: 'Conectar' }
]

// ─── FB SDK loader ────────────────────────────────────────────────────────────

function loadFbSdk () {
  return new Promise((resolve) => {
    if (window.FB) return resolve(window.FB)

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: false,
        version: 'v21.0',
        channelUrl: META_CHANNEL_URL
      })
      resolve(window.FB)
    }

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script')
      script.id = 'facebook-jssdk'
      script.src = 'https://connect.facebook.net/en_US/sdk.js'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  })
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractMetaCode (error) {
  const data = error?.response?.data
  if (data?.metaCode) return data.metaCode
  const match = data?.message?.match(/\b(190|200|100|803|10)\b/)
  return match ? Number(match[1]) : null
}

function formatDate (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
}

// ─── Wizard stepper ───────────────────────────────────────────────────────────

function WizardStepper ({ current }) {
  return (
    <div className='cw-stepper'>
      {WIZARD_STEPS.map((step, i) => {
        const idx = i + 1
        const done = idx < current
        const active = idx === current
        return (
          <React.Fragment key={idx}>
            <div className='cw-stepper-item'>
              <div className={`cw-stepper-circle ${active ? 'cw-stepper-circle--active' : ''} ${done ? 'cw-stepper-circle--done' : ''}`}>
                {done
                  ? <svg width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M2 6l3 3 5-5' stroke='white' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>
                  : idx}
              </div>
              <span className={`cw-stepper-label ${active ? 'cw-stepper-label--active' : ''}`}>{step.label}</span>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`cw-stepper-line ${done ? 'cw-stepper-line--done' : ''}`} />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

function HintPopover ({ hintKey, open, onToggle, onClose, content }) {
  const wrapRef = useRef(null)
  const popoverId = `cw-hint-popover-${hintKey}`

  useEffect(() => {
    if (!open) return

    function onPointerDown (e) {
      const wrap = wrapRef.current
      if (!wrap) return
      if (wrap.contains(e.target)) return
      onClose?.()
    }

    function onKeyDown (e) {
      if (e.key === 'Escape') onClose?.()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  return (
    <span className='cw-hint-popover-wrap' ref={wrapRef}>
      <button
        type='button'
        className='cw-hint-btn'
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={onToggle}
      >
        <InfoIcon className='icon icon--info' aria-hidden='true' focusable='false' />
        <span className='cw-sr-only'>Ver ayuda</span>
      </button>
      {open && (
        <div className='cw-hint-popover' role='dialog' id={popoverId}>
          {content}
        </div>
      )}
    </span>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

function TemplatesTab () {
  const client = useSelector((state) => state.client)
  const hasWhatsApp = !!client?.whatsapp?.isActive

  // view: 'disconnected' | 'verifying' | 'connected' | 'error'
  const [view, setView] = useState(hasWhatsApp ? 'connected' : 'disconnected')
  const [connection, setConnection] = useState(null)
  const [errorInfo, setErrorInfo] = useState(null)

  // Wizard state (only relevant when view === 'disconnected')
  const [wizardStep, setWizardStep] = useState(1)
  const [step1Checked, setStep1Checked] = useState(false)
  const [step2Checked, setStep2Checked] = useState(false)
  const [showManualForm, setShowManualForm] = useState(false)

  // Manual form
  const [waForm, setWaForm] = useState(EMPTY_WA)
  const [activeHint, setActiveHint] = useState(null)

  const [disconnecting, setDisconnecting] = useState(false)
  const [sdkLoading, setSdkLoading] = useState(false)

  // Captures wabaId + phoneNumberId from the WA_EMBEDDED_SIGNUP window.message event
  const embeddedSignupData = useRef({})

  // ── Load connection data if already connected ───────────────────────────────
  useEffect(() => {
    if (hasWhatsApp) {
      getWhatsAppConnection(client._id)
        .then(res => setConnection(res.data?.whatsapp ?? res.data))
        .catch(() => setConnection(client.whatsapp))
    }
  }, [])

  // ── Listen for WA_EMBEDDED_SIGNUP message from Meta popup ──────────────────
  useEffect(() => {
    function handleMessage (event) {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'FINISH') {
          embeddedSignupData.current = {
            wabaId: data?.data?.waba_id,
            phoneNumberId: data?.data?.phone_number_id
          }
        }
      } catch {
        // non-JSON messages from FB are normal
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  function resetWizard () {
    setWizardStep(1)
    setStep1Checked(false)
    setStep2Checked(false)
    setShowManualForm(false)
    setWaForm(EMPTY_WA)
    setActiveHint(null)
  }

  function setWaField (key, value) {
    setWaForm(prev => ({ ...prev, [key]: value }))
  }

  // ── Embedded Signup flow ────────────────────────────────────────────────────
  async function handleEmbeddedSignup () {
    setSdkLoading(true)
    let FB
    try {
      FB = await loadFbSdk()
    } catch {
      toast.error('No se pudo cargar el SDK de Meta. Verifica tu conexión.')
      setSdkLoading(false)
      return
    }
    setSdkLoading(false)

    // FB.login callback must be a regular (non-async) function — the SDK validates this.
    // Async logic is handled in a separate promise chain.
    FB.login((response) => {
      const code = response?.authResponse?.code
      if (!code) return // user cancelled

      const { wabaId, phoneNumberId } = embeddedSignupData.current
      if (!wabaId || !phoneNumberId) {
        toast.error('No se recibió información del WABA. Intenta de nuevo.')
        return
      }

      setView('verifying')

      connectWhatsAppOAuth(client._id, { code, wabaId, phoneNumberId })
        .then(() => getWhatsAppConnection(client._id))
        .then(res => {
          setConnection(res.data?.whatsapp ?? res.data)
          toast.success('¡WhatsApp Business conectado correctamente!')
          embeddedSignupData.current = {}
          resetWizard()
          setView('connected')
        })
        .catch(error => {
          const metaCode = extractMetaCode(error)
          const message = META_ERROR_MESSAGES[metaCode] ||
            error?.response?.data?.message ||
            'Error al conectar WhatsApp. Intenta de nuevo.'
          setErrorInfo({ message, code: metaCode, action: 'connect' })
          setView('error')
        })
    }, {
      scope: 'business_management,whatsapp_business_management',
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        feature: 'whatsapp_embedded_signup',
        sessionInfoVersion: 2
      }
    })
  }

  // ── Manual connect (fallback) ───────────────────────────────────────────────
  async function handleManualConnect (e) {
    e.preventDefault()
    setView('verifying')
    try {
      await connectWhatsApp(client._id, waForm)
      const res = await getWhatsAppConnection(client._id)
      setConnection(res.data?.whatsapp ?? res.data)
      toast.success('WhatsApp Business conectado correctamente')
      resetWizard()
      setView('connected')
    } catch (error) {
      const metaCode = extractMetaCode(error)
      const message = META_ERROR_MESSAGES[metaCode] ||
        error?.response?.data?.message ||
        'Error al conectar WhatsApp. Verifica las credenciales.'
      setErrorInfo({ message, code: metaCode, action: 'connect' })
      setView('error')
    }
  }

  // ── Verify ──────────────────────────────────────────────────────────────────
  const handleVerify = useCallback(async () => {
    setView('verifying')
    try {
      await verifyWhatsAppConnection(client._id)
      const res = await getWhatsAppConnection(client._id)
      setConnection(res.data?.whatsapp ?? res.data)
      toast.success('Conexión verificada correctamente')
      setView('connected')
    } catch (error) {
      const metaCode = extractMetaCode(error)
      const message = META_ERROR_MESSAGES[metaCode] ||
        error?.response?.data?.message ||
        'No se pudo verificar la conexión con Meta.'
      setErrorInfo({ message, code: metaCode, action: 'verify' })
      setView('error')
    }
  }, [client._id])

  // ── Disconnect ──────────────────────────────────────────────────────────────
  async function handleDisconnect () {
    if (!window.confirm('¿Seguro que quieres desconectar WhatsApp Business? Los templates existentes se conservarán.')) return
    setDisconnecting(true)
    try {
      await disconnectWhatsApp(client._id)
      toast.success('WhatsApp Business desconectado')
      setConnection(null)
      resetWizard()
      setView('disconnected')
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error al desconectar')
    } finally {
      setDisconnecting(false)
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className='templates-tab'>

      {/* ── Wizard de configuración ─────────────────────────────────────────── */}
      {view === 'disconnected' && !showManualForm && (
        <div className='connect-wa-card config-card'>
          <div className='cw-wizard-header'>
            <h5>Conectar WhatsApp Business API</h5>
            <p className='cw-wizard-subtitle'>
              Envía recordatorios y confirmaciones de cita directamente a los pacientes de tu clínica.
            </p>
          </div>

          <WizardStepper current={wizardStep} />

          {/* Step 1: Facebook Business Manager */}
          {wizardStep === 1 && (
            <div className='cw-wizard-body'>
              <div className='cw-wizard-step-content'>
                <div>
                  <h6>Facebook Business Manager</h6>
                  <p>Necesitas iniciar sesión con tu cuenta de Facebook y tener acceso al Business Manager de tu empresa.</p>
                  <a href='https://business.facebook.com' target='_blank' rel='noopener noreferrer' className='cw-guide-link'>
                    ¿No tienes una? Crear cuenta →
                  </a>
                </div>
                <div className='cw-wizard-illus'>
                  <FacebookIcon className='icon icon--ki-blue' aria-label='facebook-icon' />
                </div>
              </div>
              <label className='cw-prereq-check'>
                <input
                  type='checkbox'
                  checked={step1Checked}
                  onChange={e => setStep1Checked(e.target.checked)}
                />
                <span>Tengo una cuenta de Facebook y acceso al Business Manager de mi empresa</span>
              </label>
            </div>
          )}

          {/* Step 2: Número de teléfono */}
          {wizardStep === 2 && (
            <div className='cw-wizard-body'>
              <div className='cw-wizard-step-content'>
                <div>
                  <h6>Número de teléfono para WhatsApp</h6>
                  <ul className='cw-req-list'>
                    <li>Debe ser <strong>propiedad de la clínica</strong> — no uses tu número personal</li>
                    <li>Puede estar registrado en WhatsApp Messenger o Business App <a href='https://faq.whatsapp.com/1068009583948133' target='_blank' rel='noopener noreferrer' className='cw-guide-link'>(cómo desvincularlo)</a></li>
                    <li>Debes poder recibir <strong>llamadas o SMS</strong> para verificación</li>
                  </ul>
                </div>
                <div className='cw-wizard-illus'>
                  <PhoneIcon className='icon icon--ki-blue' aria-label='phone-icon' />
                </div>
              </div>
              <label className='cw-prereq-check'>
                <input
                  type='checkbox'
                  checked={step2Checked}
                  onChange={e => setStep2Checked(e.target.checked)}
                />
                <span>Tengo un número que cumple los requisitos anteriores</span>
              </label>
            </div>
          )}

          {/* Step 3: Crear canal */}
          {wizardStep === 3 && (
            <div className='cw-wizard-body'>
              <div className='cw-wizard-step-content'>
                <div>
                  <h6>Crear tu canal de WhatsApp</h6>
                  <p>
                    Serás dirigido al Business Manager de Meta. Completa los pasos en el popup para crear y conectar tu cuenta de WhatsApp Business.
                  </p>
                </div>
                <div className='cw-wizard-illus'>
                  <WhatsappIcon className='icon icon--ki-blue' aria-label='whatsapp-icon' />
                </div>
              </div>

              <button
                className='btn-connect-wa btn-connect-wa--meta cw-wizard-cta'
                onClick={handleEmbeddedSignup}
                disabled={sdkLoading}
              >
                <svg width='18' height='18' viewBox='0 0 24 24' fill='white' style={{ marginRight: 8, flexShrink: 0 }}>
                  <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                </svg>
                {sdkLoading ? 'Cargando...' : 'Conectar canal de WhatsApp'}
              </button>

              <button className='cw-manual-link' onClick={() => setShowManualForm(true)}>
                Tengo un System User token, configurar manualmente →
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className='cw-wizard-nav'>
            {wizardStep > 1 && (
              <button className='btn-cancel-wa' onClick={() => setWizardStep(s => s - 1)}>
                Atrás
              </button>
            )}
            {wizardStep < 3 && (
              <button
                className='btn-connect-wa'
                onClick={() => setWizardStep(s => s + 1)}
                disabled={wizardStep === 1 ? !step1Checked : !step2Checked}
              >
                Siguiente: {WIZARD_STEPS[wizardStep].label}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Formulario manual ───────────────────────────────────────────────── */}
      {view === 'disconnected' && showManualForm && (
        <div className='connect-wa-card'>
          <div className='cw-form-header'>
            <h5>Configurar manualmente</h5>
            <span className='cw-form-subtitle'>
              Recomendado para <strong>System User tokens</strong> (sin expiración).
            </span>
          </div>
          <form onSubmit={handleManualConnect}>
            <div className='connect-wa-grid'>
              {[
                { key: 'wabaId', label: 'WABA ID', required: true, placeholder: '123456789012345', type: 'number' },
                { key: 'phoneNumberId', label: 'Phone Number ID', required: true, placeholder: '987654321098765', type: 'number' },
                { key: 'displayPhoneNumber', label: 'Número de teléfono', required: true, placeholder: '+573001234567', type: 'number' },
                { key: 'templatesNamespace', label: 'Templates Namespace', required: false, placeholder: 'Auto-detectado si se deja vacío', type: 'text' },
                { key: 'businessId', label: 'Business ID', required: false, placeholder: '111222333 (opcional)', type: 'number' }
              ].map(({ key, label, required, placeholder, type }) => (
                <div className='cw-field' key={key}>
                  <label className='form-label' htmlFor={key}>
                    {label} {required && '*'}
                    <HintPopover
                      hintKey={key}
                      open={activeHint === key}
                      onToggle={() => setActiveHint(activeHint === key ? null : key)}
                      onClose={() => setActiveHint(null)}
                      content={FIELD_HINTS[key]}
                    />
                  </label>
                  <input
                    id={key}
                    type={type}
                    className='form-control'
                    required={required}
                    value={waForm[key]}
                    onChange={e => setWaField(key, e.target.value)}
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <div className='cw-field cw-field--full'>
                <label className='form-label' htmlFor='accessToken'>
                  Access Token *
                  <HintPopover
                    hintKey='accessToken'
                    open={activeHint === 'accessToken'}
                    onToggle={() => setActiveHint(activeHint === 'accessToken' ? null : 'accessToken')}
                    onClose={() => setActiveHint(null)}
                    content={FIELD_HINTS.accessToken}
                  />

                </label>
                <input
                  id='accessToken'
                  className='form-control'
                  required
                  type='password'
                  value={waForm.accessToken}
                  onChange={e => setWaField('accessToken', e.target.value)}
                  placeholder='EAAxxxxx...'
                  autoComplete='new-password'
                />
                <div className='cw-token-warning'>
                  ⚠️ Usa un <strong>System User token</strong> (sin expiración). Los tokens personales expiran en 1–60 días y romperán la integración.
                </div>
              </div>
            </div>
            <div className='cw-wizard-nav'>
              <button type='button' className='btn-cancel-wa' onClick={() => setShowManualForm(false)}>
                Cancelar
              </button>
              <button type='submit' className='btn-connect-wa'>Guardar credenciales</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Verificando ─────────────────────────────────────────────────────── */}
      {view === 'verifying' && (
        <div className='connect-wa-card'>
          <div className='cw-verifying'>
            <div className='cw-spinner' />
            <span>Verificando conexión con Meta...</span>
          </div>
        </div>
      )}

      {/* ── Conectado ───────────────────────────────────────────────────────── */}
      {view === 'connected' && (
        <div className='connect-wa-card'>
          <div className='templates-tab-header'>
            <div className='templates-tab-header-left'>
              <h4>WhatsApp Message Templates</h4>
              <span className='wa-connected-badge'>
                <span className='dot' />
                Conectado
              </span>
            </div>
            <div className='cw-connected-actions'>
              <button className='btn-verify-wa' onClick={handleVerify}>
                Verificar conexión
              </button>
              <button
                className='btn-disconnect-wa'
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? 'Desconectando...' : 'Desconectar'}
              </button>
            </div>
          </div>

          {connection && (
            <div className='cw-connection-meta'>
              <div className='cw-meta-item'>
                <span className='cw-meta-label'>Número</span>
                <span className='cw-meta-value'>{connection.displayPhoneNumber || '—'}</span>
              </div>
              <div className='cw-meta-item'>
                <span className='cw-meta-label'>WABA ID</span>
                <span className='cw-meta-value'>{connection.wabaId || '—'}</span>
              </div>
              <div className='cw-meta-item'>
                <span className='cw-meta-label'>Token</span>
                <span className='cw-meta-value'>
                  {connection.tokenLast4 ? `···· ${connection.tokenLast4}` : '—'}
                </span>
              </div>
              <div className='cw-meta-item'>
                <span className='cw-meta-label'>Última verificación</span>
                <span className='cw-meta-value'>{formatDate(connection.lastVerifiedAt)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {view === 'error' && errorInfo && (
        <div className='connect-wa-card'>
          <div className='cw-error-state'>
            <div className='cw-error-icon'>✕</div>
            <div className='cw-error-body'>
              <h5>No se pudo conectar con Meta</h5>
              <p>{errorInfo.message}</p>
              {errorInfo.code && (
                <span className='cw-error-code'>Código Meta: {errorInfo.code}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {errorInfo.action === 'verify'
              ? (
                <button className='btn-connect-wa' onClick={() => setView('connected')}>Volver</button>
                )
              : (
                <>
                  <button
                    className='btn-connect-wa btn-connect-wa--meta'
                    onClick={() => { setView('disconnected'); setWizardStep(3) }}
                  >
                    Reconectar con Meta
                  </button>
                  <button className='btn-cancel-wa' onClick={() => { setView('disconnected'); resetWizard() }}>
                    Cancelar
                  </button>
                </>
                )}
          </div>
        </div>
      )}

      {/* Templates table — always visible regardless of connection state */}
      {view !== 'verifying' && (
        <>
          {view !== 'connected' && (
            <div className='cw-disconnected-banner'>
              <span>⚠️</span>
              <span>Conecta WhatsApp para poder enviar estos templates a pacientes. Puedes crearlos y editarlos ahora.</span>
            </div>
          )}
          <TemplatesTable />
        </>
      )}

    </div>
  )
}

export default TemplatesTab
